import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Quiz from './Quiz';
import './QuizStyles.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../../services/Api';

/**
 * QuizContainer
 * Manages quiz state, API communication, and SEO meta tags.
 */
const QuizContainer = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * Handle lead submission from the Quiz component.
     * Saves the lead to the backend and calls onSuccess callback.
     */
    const handleLeadSubmit = async (leadData, onSuccess) => {
        setIsSubmitting(true);

        try {
            const payload = {
                name: leadData.name,
                email: leadData.email,
                phone: leadData.phone,
                quizCompleted: true,
                quizVersion: leadData.quizVersion,
                irisColor: leadData.irisColor,
                fiberPattern: leadData.fiberPattern,
                pigmentation: leadData.pigmentation,
                rings: leadData.rings,
                selectedIrisModel: leadData.selectedIrisModel,
                assignedProfile: leadData.assignedProfile,
                cerebralProfile: leadData.cerebralProfile,
                emotionalProfile: leadData.emotionalProfile,
                behaviorProfile: leadData.behaviorProfile,
                convertedToAnalysis: false,
                localCreatedAt: new Date().toISOString(),
                localTimezoneOffsetMinutes: -new Date().getTimezoneOffset(),
                quizAnalytics: {
                    startedAt: new Date().toISOString(),
                    completedAt: new Date().toISOString(),
                    userAgent: navigator.userAgent,
                    referrer: document.referrer || '',
                    utm: {
                        source: new URLSearchParams(window.location.search).get('utm_source') || '',
                        medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
                        campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
                    },
                },
            };

            // Try to save lead to backend
            try {
                await Api.post('/quiz/lead', payload);
                toast.success('Seu resultado foi enviado! Já pode conferir no seu e-mail ou seguir aqui!');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (apiError) {
                // Log but don't block the user experience
                console.warn('Failed to save quiz lead to backend:', apiError);
            }

            // Always show result even if backend fails
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error submitting quiz lead:', error);
            toast.error('Ocorreu um erro. Tente novamente.');

            // Still show result even on error
            if (onSuccess) onSuccess();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Descubra seu Perfil Mental, Emocional e Comportamental | E-IRIS</title>
                <meta
                    name="description"
                    content="Realize uma autoavaliação rápida e descubra gratuitamente seu perfil mental, emocional e comportamental baseado nas características únicas da sua íris."
                />
                <meta property="og:title" content="Descubra seu Perfil Mental, Emocional e Comportamental | E-IRIS" />
                <meta
                    property="og:description"
                    content="Seu modo de pensar, sentir e agir pode estar relacionado às características únicas da sua íris. Faça o quiz gratuito e descubra!"
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://analysis.eiris.app/descubra-seu-perfil" />
                <meta property="og:image" content="https://storage.googleapis.com/e-iris-businesses/621b8185e0b4ed2240ce0f4b/logo.png" />
                <meta property="og:site_name" content="E-IRIS" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Descubra seu Perfil Mental, Emocional e Comportamental | E-IRIS" />
                <meta
                    name="twitter:description"
                    content="Realize uma autoavaliação rápida e descubra gratuitamente seu perfil predominante."
                />
                <link rel="canonical" href="https://analysis.eiris.app/descubra-seu-perfil" />
            </Helmet>

            <Quiz onLeadSubmit={handleLeadSubmit} isSubmitting={isSubmitting} />
            <ToastContainer position="top-right" autoClose={5000} />
        </>
    );
};

export default QuizContainer;
